import {
  LanguageSwitcher,
  ChakraModalCloseButton as ModalCloseButton,
  ChakraBox as Box,
  chakraUseBreakpointValue as useBreakpointValue,
} from "@airhelp/react-ui-components";
import { useTranslations } from "contexts/LocalizationContext/hooks";
import languages from "config/languages";
import { useModal } from "contexts/ModalContext";
import { useLanguage } from "contexts/LocalizationContext";
import { useCookies } from "react-cookie";
import { extractDomain, getExpDateByDays } from "utils/cookiesUtils";
import {
  LANGUAGE_MODAL_SELECT_LANGUAGE_GTM,
  LANGUAGE_MODAL_SELECT_LANGUAGE_MP,
} from "modules/home-page/trackingConsts";
import { trackEvents } from "utils/trackingUtils";
import { useRouter } from "next/router";
import { useCMSLanguageData, usePageName } from "contexts/DataContext/hooks";
import { getPageUrl } from "utils/getPageUrl";
import type { ListItem } from "@airhelp/react-ui-components/dist/components/airhelp/LanguageSwitcher/index.types";
import buildLanguagesLists from "./utility";
import { LANGUAGE_COOKIE_EXPIRATION, LANGUAGE_COOKIE_NAME } from "./config";

const LanguageSwitcherModal = () => {
  const { push } = useRouter();
  const translations = useTranslations();
  const { onClose } = useModal();
  const currentLanguage = useLanguage();
  const [, setCookie] = useCookies([LANGUAGE_COOKIE_NAME]);
  const cmsLanguagesData = useCMSLanguageData();
  const pageName = usePageName();

  const { selectLanguages, suggestedLanguages } = buildLanguagesLists(
    languages,
    pageName,
    cmsLanguagesData,
    getPageUrl
  );

  const createLanguageCookie = (language: string) => {
    const domain = extractDomain();
    setCookie(LANGUAGE_COOKIE_NAME, language, {
      domain,
      path: "/",
      expires: getExpDateByDays(LANGUAGE_COOKIE_EXPIRATION),
    });
  };

  const onItemClick = ({ id, href }: ListItem & { href: string }) => {
    createLanguageCookie(id);
    onClose();
    push(href);
    trackEvents({
      gtmEvent: [...LANGUAGE_MODAL_SELECT_LANGUAGE_GTM, id],
      mixpanelEvent: `${LANGUAGE_MODAL_SELECT_LANGUAGE_MP}_${id}`,
    });
  };

  const content = [
    {
      listHeading: translations("suggested_languages"),
      listItems: suggestedLanguages,
    },
    {
      listHeading: translations("select_language"),
      listItems: selectLanguages,
      listAutoflow: { base: "row", lg: "column" },
    },
  ];

  return (
    <Box rounded={{ base: "none", lg: "md" }} margin={6} pt={12}>
      <ModalCloseButton
        position="absolute"
        top={2}
        right={2}
        color="primary.900"
        _hover={{ color: "primary.500", bg: "white" }}
        _active={{ color: "primary.700", bg: "white" }}
        size={useBreakpointValue({ base: "lg", lg: "md" })}
      />

      <LanguageSwitcher activeItems={[currentLanguage]} onItemClick={onItemClick} lists={content} />
    </Box>
  );
};

export default LanguageSwitcherModal;
